import React from 'react';
import { Helmet } from 'react-helmet';

const RulePage = () => {
  return (
    <div>
      <Helmet>
        <title>Rules &amp; Information | Red Rock Ponds RV Resort</title>
        <meta name="description" content="Campground rules, reservation information, fees, cancellation policy, and swim area rules for Red Rock Ponds RV Resort in Holley, NY." />
      </Helmet>
      <section className="section">
        <div className="container">
          <div className="content">
            <h1>Rules & Information</h1>
            <p>These rules are in place for your safety and the well-being of those around you.
              Please respect these rules, our staff, and other campers. </p>
            <h2>Nature of Stay</h2>
            <p>All stays at Red Rock Ponds RV Resort are for recreational camping purposes only and
              are provided as a revocable license to occupy an assigned campsite for the reserved
              period.
            </p>
            <p>A reservation or stay does not create a lease, tenancy, or landlord-tenant
              relationship. 
            </p>
            <p>All stays are subject to the applicable Red Rock Ponds Licensing Agreement.</p>
            <h2>Resort Rules</h2>
            <ol>
              <li><strong>SPEED LIMIT</strong> - 5 MPH. Offenders may be removed without refund.</li>
              <li><strong>QUIET HOURS</strong> - 10:00 PM to 8:00 AM. No loud noise, music, or generators
                during quiet hours.
              </li>
              <li><strong>CHECK-IN / CHECK-OUT</strong> - Check-in is 1:00 PM. Check-out is 11:00 AM.
                Early check-in or late check-out may be available for an additional fee.
              </li>
              <li><strong>GATE PASS</strong> - Gate access is for registered campers only. Do not use your
                pass to let in visitors during business hours. After-hours visitors must be registered
                the next business day. Violators may be fined, have access disabled, and/or be
                removed. The gate allows one vehicle at a time. Do not tailgate.
              </li>
              <li><strong>ALCOHOL &amp; DRUGS</strong> - Drink responsibly. No underage drinking, illegal
                drugs, or belligerent behavior. Violators will be removed without refund.
              </li>
              <li><strong>FIREARMS &amp; FIREWORKS</strong> - Firearms, fireworks, and Chinese lanterns are
                not permitted on the premises.
              </li>
              <li><strong>SITE ASSIGNMENT</strong> - Changing campsites without permission is prohibited.</li>
              <li><strong>USE OF CAMPSITE</strong> - Each site includes up to 2 adults, 3 children under 18,
                and 2 pets. Additional guests are $5.00 per night. One camping unit is permitted per
                site. Tents may be permitted with prior approval at $25 per night for a small tent
                and $35 per night for a large tent. Only registered campers may use the site unless
                otherwise permitted by management.
              </li>
              <li><strong>CAMPING ETIQUETTE</strong> - Campsites must be kept neat, orderly, and free from 
                trash. Do not leave trash outside overnight. Trash must be taken to the dumpsters.
                Respect your neighbors. Do not walk through other campsites.
              </li>
              <li><strong>VEHICLES &amp; GOLF CARTS</strong> - Motorized vehicles may only be operated by a
                person with a valid driver license. Do not park vehicles on grass in wet conditions.
                Only electric golf carts are allowed. Proof of insurance is required for golf carts.
                Golf carts without lights may not be driven after dark.
              </li>
              <li><strong>SEWER</strong> - Sewer connections must be sealed. No hoses dropped in. No
                dumping gray water or waste on the ground.
              </li>
              <li><strong>VISITORS</strong> - Visitors must stop at the front office to register. Visitor
                fees are $5.00 per adult and $3.00 per child under 16. You are responsible for your
                visitors&apos; actions. A visitor pass must be visible at all times. Unregistered visitors
                may be asked to leave.
              </li>
              <li><strong>PETS</strong> - Pets must be leashed and under control at all times. Clean up
                after your pet immediately. Do not leave pets unattended. Any pet causing a
                disturbance will not be allowed to stay. Pets are not allowed in buildings, the
                beach area, ponds, or playground area. Do not tie pets to trees. Proof of current
                rabies vaccination is required for dogs. Dispose of pet waste in the main dumpster
                only. Breed restrictions apply. ADA service animals are not considered pets but must
                comply with applicable health and safety requirements.
              </li>
              <li><strong>CAMPFIRES</strong> - Campfires are allowed only within the fire ring provided or
                your own approved fire pit. Fires must be kept under control and never left
                unattended. Fires must be fully out before turning in for the night. Do not move
                fire rings. Do not burn garbage.
              </li>
              <li><strong>TREES</strong> - Cutting, nailing decorations, attaching name plaques, or
                otherwise damaging trees or plant life is prohibited. Climbing trees is prohibited.
                No hammocks may be tied to trees. Free-standing hammocks are permitted.
              </li>
              <li><strong>CHILDREN</strong> - Parents are responsible for the safety and actions of their
                children. Children are not allowed in the play area or beach area after posted
                closing hours. Children under 8 must be accompanied to the restrooms. Vandalism and
                disrespect for people or property will not be tolerated.
              </li>
              <li><strong>BICYCLES</strong> - New York State bicycle laws must be followed, including
                helmet use, safe speeds, and no riding after dusk. ATVs, skateboards, razor
                scooters, and rollerblades are not permitted.
              </li>
              <li><strong>FACILITIES</strong> - No dishwashing in restrooms. No smoking in any building,
                including e-cigarettes. Do not throw cigarette butts on the grounds.
              </li>
              <li><strong>LAUNDRY</strong> - Clotheslines are not permitted at campsites. Do not use your
                awning as a clothesline. Laundry facilities are available for your convenience and
                are open 24 hours. Please do not leave clothes in washers or dryers. Change is
                available at the office.
              </li>
              <li><strong>STAKES</strong> - Due to underground utilities, no stakes may be driven into the
                ground without prior approval. Campers are responsible for any resulting damage.
              </li>
              <li><strong>INFLATABLE POOLS</strong> - Inflatable pools are not permitted due to safety
                concerns and insurance limitations.
              </li>
              <li><strong>CANCELLATION / EARLY DEPARTURE</strong> - No cancellations once checked in. No
                refunds for early departure.
              </li>
            </ol>
            <h2>Reservation Information</h2>
            <ol type="1">
              <li>Reservations are not transferable without prior approval.</li>
              <li>The minimum age to reserve and occupy a site is 21 years of age.</li>
              <li>Assigned campsite location is subject to change. While we make every effort to
                place guests on the reserved site number, a specific site number is not guaranteed.
              </li>
              <li>A valid credit card is required to make a reservation.</li>
            </ol>
            <h2>Fees &amp; Payment</h2>
            <ol type="1">
              <li>Rates include 2 adults and 3 children under 18.</li>
              <li>Additional guests are $5.00 per person per night unless otherwise stated for the
                stay type.
              </li>
              <li>Visitor fees are $5.00 per adult and $3.00 per child under 16.</li>
              <li>Tent fees are $25.00 per night for a small tent and $35.00 per night for a large
                tent.
              </li>
              <li>Rates and terms are subject to change prior to the start of the camping season.</li>
            </ol>
            <h2>Cancellation Policy</h2>
            <h6>Short-Term Reservations:</h6>
            <ol type="1">
              <li>Cancellations made more than 10 days in advance receive a refund of deposit less a
                $15 fee.
              </li>
              <li>Cancellations made 10 days or less in advance receive a refund of deposit less
                one night at the prevailing daily rate.
              </li>
            </ol>
            <h6>Weekly / Monthly Reservations:</h6>
            <ol type="1">
              <li>Cancellations made more than 10 days in advance receive a refund of deposit less a
                $25 fee per reserved week or month, as applicable.
              </li>
              <li>Cancellations made 10 days or less in advance receive no refund.
              </li>
            </ol>
            <h6>Holiday Reservations:</h6>
            <p>Holiday reservation cancellations receive no refund.</p>
            <h6>Early Departure:</h6>
            <p>No refunds for early departure.</p>
            <h2>Swim &amp; Playground Areas</h2>
            <ol type="1">
              <li>Never swim alone. Swim only in the designated swim area.</li>
              <li>Children under 16 must be accompanied at the swim area by two parents or two adults
                over 18. One adult must remain on the beach at all times.
              </li>
              <li>Children under 16 must be with a parent at the playground.</li>
              <li>No rock or sand throwing.</li>
              <li>No glass bottles or alcoholic containers at the playground or swim area. Do not
                drink alcohol and swim.
              </li>
              <li>No pets allowed at the beach area.</li>
              <li>Ponds are for fishing only. Catch and release. No license required.</li>
              <li>Boats and life jackets must be signed out at the office before use. All persons
                using watercraft must wear a life jacket.
              </li>
              <li>In an emergency, notify the facility operator and contact help as soon as possible.
                A free phone is available at the front office.
              </li>
              <li>Use facilities only during posted hours of operation.</li>
            </ol>
            <h2>Final Note</h2>
            <p>These website rules are a summary only.</p>
            <p>Additional terms may apply depending on stay type, including short-term, extended
              stay, and seasonal licensing.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RulePage;
